// src/pages/SignupPage.tsx
import React, { useState, useEffect } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'

export default function SignupPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const emailFromQuery = searchParams.get('email') || ''
  const isTrial = searchParams.get('trial') === 'true'
  
  const [email, setEmail] = useState(emailFromQuery)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  const [error, setError] = useState<string>()
  const [message, setMessage] = useState<string>()
  const [loading, setLoading] = useState(false)

  // IMPORTANT: Redirect if no email in query params (prevents direct access)
  useEffect(() => {
    if (!emailFromQuery) {
      console.log('⚠️ Unauthorized direct access to signup page detected - redirecting to pricing')
      navigate(isTrial ? '/trial' : '/pricing')
    }
  }, [emailFromQuery, navigate, isTrial])

  // if URL has ?email=, prefill once on mount
  useEffect(() => {
    if (emailFromQuery) setEmail(emailFromQuery)
  }, [emailFromQuery])

  const handleSignup = async (e?: React.FormEvent) => {
    // Prevent default form submission if called from form onSubmit
    if (e) e.preventDefault()
    
    setError(undefined)
    setMessage(undefined)
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    // Validate password strength
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    
    setLoading(true)

    try {
      // Create user with password - webhook.ts handles profile creation
      const { error: signUpError, data } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
      })
      
      if (signUpError) {
        console.error('❌ Sign up error:', signUpError)
        setError(signUpError.message)
        return
      }
      
      console.log('✅ Auth user created:', data?.user?.id)
      console.log('✅ Profile will be created by webhook when subscription is processed')
      
      setMessage('✅ Account created! You can now log in.')
      
      // Redirect to login page after short delay
      setTimeout(() => {
        navigate('/login')
      }, 2000)
      
    } catch (err) {
      console.error('❌ Unexpected error:', err)
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  // Prevent rendering if no email parameter
  if (!emailFromQuery) {
    return null; // Don't render anything while redirecting
  }

  return (
<div data-page="signup" className="min-h-screen bg-white flex flex-col items-center justify-center p-4 relative overflow-hidden">
        {/* Atmospheric lighting effects */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full bg-primary-500/15 blur-[120px] -z-10"></div>
      <div className="absolute bottom-0 left-0 w-[800px] h-[800px] rounded-full bg-gray-300/20 blur-[120px] -z-10"></div>
      
      <div className="w-full max-w-md mb-6 relative z-10">
        <Button 
          as={Link} 
          to="/" 
          variant="ghost" 
          className="flex items-center text-gray-700 hover:text-gray-950 transition-colors !rounded-full px-6 py-2"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Home
        </Button>
      </div>
      
      <Card className="w-full max-w-md glass-card shadow-lg z-10">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-display font-bold text-gray-950">
            {isTrial ? 'Complete Your Free Trial' : 'Create Account'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isTrial && (
            <div className="mb-6 p-4 bg-primary-50 rounded-lg border border-primary-200 text-primary-700">
              <p className="text-sm">
                You're signing up for a <strong>3-day free trial</strong> with no credit card required!
              </p>
            </div>
          )}
          
          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="mt-1 w-full p-3 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="mt-1 w-full p-3 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm"
                />
                <button 
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="mt-1 w-full p-3 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm"
                />
                <button 
                  type="button"
                  onClick={toggleConfirmPasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            {error && <p className="text-red-600 text-sm">{error}</p>}
            {message && <p className="text-green-600 text-sm">{message}</p>}
            
            <Button 
              type="submit"
              disabled={!email || !password || !confirmPassword || loading} 
              fullWidth
              className={`!rounded-full py-3 text-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 ${
                !email || !password || !confirmPassword || loading 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-black hover:bg-gray-900 text-white'
              }`}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
            
            <div className="text-center text-sm text-gray-700 mt-4">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-700 hover:text-primary-600 transition-colors hover:underline">
                Log in
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}